import type { DocumentationSignature } from './safetyRepWork';
// Work Environment Survey Types (Psykososialt Arbeidsmiljø)
// Based on Norwegian Work Environment Act § 4-3
// Updated to comply with changes effective 01.01.2026

import type { FactorDimension } from './hms';
import type { NextDeadline } from './hrWorkEnvironment';

export enum WorkEnvironmentSurveyStatus {
  DRAFT = 'DRAFT',
  SURVEY_SENT = 'SURVEY_SENT',
  SURVEY_IN_PROGRESS = 'SURVEY_IN_PROGRESS',
  AWAITING_RISK_CALC = 'AWAITING_RISK_CALC',
  RISK_CALCULATED = 'RISK_CALCULATED',
  RISK_ASSESSED = 'RISK_ASSESSED',
  ACTION_PROPOSAL_OPEN = 'ACTION_PROPOSAL_OPEN',
  ACTION_VOTING_OPEN = 'ACTION_VOTING_OPEN',
  ACTION_PLAN_PENDING = 'ACTION_PLAN_PENDING',
  ACTION_PLAN_COMPLETED = 'ACTION_PLAN_COMPLETED',
  EVALUATING = 'EVALUATING',
  INSUFFICIENT_RESPONSES = 'INSUFFICIENT_RESPONSES',
  ARCHIVED = 'ARCHIVED',
}

/**
 * What the viewer may do on a survey, from their role. The status of the survey still decides
 * when. The safety representative approves and comments on plans, adds data, follows up the
 * measures and decides on remaining risk; the team leader runs the rest of the work.
 */
export type SurveyAction =
  | 'assess'
  | 'addData'
  | 'advance'
  | 'propose'
  | 'hideProposal'
  | 'makePlan'
  | 'reviewPlan'
  | 'followUp'
  | 'share'
  | 'startEvaluation'
  | 'decideResidual'
  | 'requestSignature'
  | 'archive';

export type WorkEnvironmentSurvey = {
  id: number;
  uuid: string;
  title: string;
  description: string | null;
  isActive: boolean;
  status: WorkEnvironmentSurveyStatus;
  createdAt: string;
  tenantId: number;
  teamId: number;
  hmsTemplateVersionId: number;
  responseRate: number;
  daysBeforeDeadlineToNotify?: number;
  deadlineInDays: number;
  expectedParticipantsCount: number | null;
  nextScheduledMeasurement: string | null;
  hasSentSurveyResultNotification: boolean;
  /** Set once the survey has closed and its risk was calculated */
  riskCalculatedAt: string | null;
  /** Set when the survey goes on to the action plan step */
  actionPlanOpenedAt: string | null;
  /** Set on an evaluation, to the survey it measures again */
  baselineSurveyId: number | null;
  /** HR and the safety representative are not in the team, so the name comes with the survey */
  teamName: string;
  /** What the viewer may do on the survey, from their role. Missing from older backends. */
  actions?: SurveyAction[];
  /** The team leader, HR and the safety representative see the risk. A team member gets it shared later. */
  canViewRisk?: boolean;
  /** When the team gets the invitation, after «Send now»: right away, or when the working hours of the team start */
  invitationSendsAt?: string;
  /** The viewer is the main safety representative, who follows up because the team leader is the team's safety rep */
  insteadOfLeader?: boolean;
};

export type QuestionnaireQuestion = {
  /** Stable reference used when answering */
  ref: string;
  text: string;
  help: string | null;
  highIsGood: boolean;
  isIncident?: boolean;
};

/** Required by the Working Environment Act, part of the Trustpulse standard, or added by the organisation */
export type FactorKind = 'required' | 'standard' | 'custom';

export type QuestionnaireFactor = {
  key: string;
  name: string;
  description: string;
  kind: FactorKind;
  /** Harassment, sexual harassment and violence */
  sensitive: boolean;
  /** The employee may choose not to answer the part */
  skippable: boolean;
  questions: QuestionnaireQuestion[];
};

/** The survey as the employee sees it, in one language */
export type Questionnaire = {
  language: string;
  direction: 'ltr' | 'rtl';
  /** Bokmål first, then the languages in the template */
  languages: string[];
  factors: QuestionnaireFactor[];
  /** The words for 1–5, in the language of the questions: how often, and how many times for an incident */
  scale: { answers: string[]; incidents: string[] };
};

/** An approved HMS template version the team leader can choose */
export type SurveyTemplateVersion = {
  id: number;
  version: number;
  recommended: boolean;
  approvedAt: string;
  factors: QuestionnaireFactor[];
};

export type CreateWorkEnvironmentSurveyRequest = {
  title: string;
  description?: string;
  teamId: number;
  templateVersionId: number;
  scheduledTime?: string;
  frequency?: string;
  deadlineInDays?: number;
  actionProposalDeadlineInDays?: number;
  actionVotingDeadlineInDays?: number;
  daysBeforeDeadlineToNotify?: number;
  expectedParticipantsCount?: number;
  nextScheduledMeasurement?: string;
};

export type SubmitWorkEnvironmentSurveyRequest = {
  uuid: string;
  answers: Array<{ ref: string; value: number }>;
  /** The sensitive parts the employee chose not to answer, by factor key */
  skipped: string[];
};

export type WorkEnvironmentSurveyProgress = {
  status: WorkEnvironmentSurveyStatus;
  responses: number;
  expected: number;
  responseDeadline: string | null;
  lastRemindedAt: string | null;
  canRemind: boolean;
};

export type HmsRiskLevel = 'low' | 'moderate' | 'high';

export type RiskArea = {
  key: string;
  name: string;
  description: string;
  kind: FactorKind;
  dimension: FactorDimension;
  /** Harassment, sexual harassment and violence */
  sensitive: boolean;
};

export type RiskQuestion = {
  ref: string;
  text: string;
  highIsGood: boolean;
  /** How often something has happened the last 12 months. Missing from older backends. */
  isIncident?: boolean;
  mean: number;
  standardDeviation: number;
};

export type AssessedArea = RiskArea & {
  status: 'assessed';
  consequence: number;
  likelihood: number;
  score: number;
  level: HmsRiskLevel;
  /** One person reported what HR has set to always give high risk */
  oneReport: boolean;
  responses: number;
  /** Only set when enough chose not to answer, so no one can be identified */
  skipped: number | null;
  /** 1–5, high means more strain on a strain and a stronger resource on a resource */
  dimensionScore: number;
  questions: RiskQuestion[];
  /** The cell the area was moved to in the assessment with the safety representative */
  adjustment: (Placement & { score: number; reason: string }) | null;
  adjustedLevel: HmsRiskLevel | null;
};

export type UnassessedArea = RiskArea & {
  status: 'tooFewResponses';
  requiresFollowUp: boolean;
  skipped: number | null;
};

export type RiskResult =
  | {
      status: 'assessed';
      responses: number;
      minResponses: number;
      /** HR's thresholds, so the matrix can colour every cell */
      levels: { moderateFrom: number; highFrom: number };
      areas: AssessedArea[];
      unassessed: UnassessedArea[];
    }
  | {
      status: 'insufficientResponses';
      responses: number;
      minResponses: number;
      requiresFollowUp: RiskArea[];
    };

export type DimensionSummary = {
  areas: Array<{ key: string; name: string; score: number }>;
  average: number;
};

export type Placement = { consequence: number; likelihood: number };

export type RiskAdjustment = Placement & { factorKey: string; reason: string };

type SavedBy = { savedBy: string | null; savedAt: string };

/** Who went through the risk with the team leader */
/** TEAM_LEADER where the organisation does not use safety representatives */
export type ReviewParticipation = 'SAFETY_REP' | 'EMPLOYEE_REP' | 'NONE' | 'TEAM_LEADER';

export type RiskAssessment = {
  participation: ReviewParticipation;
  representativeRole: string | null;
  absenceReason: string | null;
  shortLongTerm: string;
  combined: string | null;
  adjustments: RiskAdjustment[];
};

/** One move in the matrix, as the log keeps it. A move back to the calculated cell has no reason. */
export type AdjustmentLogEntry = {
  factorKey: string;
  before: Placement & { level: HmsRiskLevel };
  after: Placement & { level: HmsRiskLevel };
  reason: string | null;
  changedBy: string | null;
  changedByRole: RiskActivity['role'];
  changedAt: string;
};

export type RiskAssessmentRequest = {
  participation: ReviewParticipation | null;
  representativeRole: string | null;
  absenceReason: string | null;
  shortLongTerm: string;
  combined: string | null;
  adjustments: RiskAdjustment[];
};

export type RiskAssessmentError =
  | { type: 'participation' | 'absenceReason' | 'shortLongTerm' | 'combined' }
  | { type: 'adjustment' | 'adjustmentReason'; factorKey: string };

export type InsufficientAssessor = 'SAFETY_REP' | 'AMU' | 'BOTH' | 'EMPLOYEE_REP' | 'TEAM_LEADER';

export type InsufficientAssessment = {
  assessedBy: InsufficientAssessor;
  interpretation: string;
  alternative: string;
};

export type InsufficientAssessmentRequest = {
  assessedBy: InsufficientAssessor | null;
  interpretation: string;
  alternative: string;
};

export type InsufficientAssessmentError = 'assessedBy' | 'interpretation' | 'alternative';

export type DataSourceType = 'SAFETY_INSPECTION' | 'EMPLOYEE_CONVERSATIONS' | 'HMS_REPORT' | 'SICK_LEAVE' | 'OTHER';

/** Other data about an area. It is shown with the answers and never changes the calculated risk. */
export type DataSource = {
  id: number;
  factorKey: string;
  type: DataSourceType;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  summary: string;
  dimension: FactorDimension;
  /** 1–5 */
  level: number;
  createdBy: string | null;
  createdAt: string;
};

export type DataSourceRequest = Omit<DataSource, 'id' | 'createdBy' | 'createdAt'>;

export type DataSourceError = 'area' | 'type' | 'title' | 'date' | 'summary' | 'dimension' | 'level';

export type DiscussionForum = 'TEAM_MEETING' | 'AMU' | 'OTHER';

/** Where and when the team discussed the proposed measures, as the inspection asks to see it */
export type ProposalDiscussion = { id: number; forum: DiscussionForum; date: string; note: string | null };

export type ProposalDiscussions = { discussions: ProposalDiscussion[]; canRegister: boolean };

export type DiscussionRequest = { forum: DiscussionForum | ''; date: string; note: string };

export type DiscussionError = 'forum' | 'date' | 'note';

/** In review the proposals are closed, and the team waits for the leader to start the highlighting */
export type MeasureStep = 'propose' | 'review' | 'highlight' | 'plan';

/** An area as the measures show it, with the level the assessment ended with */
export type MeasureArea = {
  key: string;
  name: string;
  kind: FactorKind;
  level: HmsRiskLevel;
  needsMeasures: boolean;
  proposals: number;
};

export type Proposal = {
  id: number;
  factorKey: string;
  title: string;
  reason: string | null;
  /** Null for the team's anonymous proposals */
  createdBy: string | null;
  fromLeader: boolean;
  hidden: { reason: string; by: string | null; at: string } | null;
  highlights: number;
  createdAt: string;
};

export type WorkEnvironmentMeasures = {
  step: MeasureStep;
  deadline: string | null;
  areas: MeasureArea[];
  proposals: Proposal[];
  voters: number;
  expected: number;
  maxHighlights: number;
};

/** A sensitive area has no level for the team, so nobody can guess who answered what */
export type TeamMeasureArea = Omit<MeasureArea, 'level'> & { level: HmsRiskLevel | null };

/** What the team sees through the link. Proposals only once they can pick, without counts or names. */
export type TeamMeasures = {
  title: string;
  team: string;
  step: MeasureStep | null;
  deadline: string | null;
  maxHighlights: number;
  hasSafetyRep: boolean;
  areas: TeamMeasureArea[];
  proposals: Array<Pick<Proposal, 'id' | 'factorKey' | 'title' | 'reason' | 'fromLeader'>>;
  picks: number[];
};

export type ProposalRequest = { factorKey: string; title: string; reason: string };

export type ProposalError = 'area' | 'title' | 'titleLength' | 'reasonLength';

export type AreaDecisionType = 'PLAN_LATER' | 'NOT_NOW' | 'NEXT_SURVEY' | 'ACCEPT';

export type PlanFrequency = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY';

export type MeasureStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

export type PlanMeasure = {
  id: number;
  title: string;
  description: string | null;
  /** Null until chosen in a draft, or when the owner's user is deleted */
  ownerId: number | null;
  owner: string | null;
  /** YYYY-MM-DD */
  dueDate: string;
  status: MeasureStatus;
  completedAt: string | null;
  proposalIds: number[];
  /** The areas of the plan the measure helps with */
  factorKeys: string[];
};

/** An area a plan deals with, with its own goal */
export type ActionPlanArea = { factorKey: string; goal: string | null };

/** One plan can deal with several areas. Each area it covers shows the same plan. */
export type ActionPlan = {
  id: number;
  areas: ActionPlanArea[];
  frequency: PlanFrequency | null;
  measureSafety: boolean;
  measurePulse: boolean;
  evaluationDate: string | null;
  reviewRequestedAt: string | null;
  reviewRemindedAt: string | null;
  /** Without a deviation, the safety representative approved it */
  approved: { at: string; by: string | null; deviation: string | null } | null;
  /** A plan sent to the safety representative waits until it is approved or withdrawn */
  approvalState: 'draft' | 'awaitingApproval' | 'approved';
  canRemind: boolean;
  /** When an approved plan was changed and needs a new approval */
  reopenedAt: string | null;
  measures: PlanMeasure[];
  comments: Array<{ id: number; author: string | null; role: string; text: string; at: string }>;
  /** Newest first. YYYY-MM-DD. */
  reviews: Array<{ on: string; note: string | null; by: string | null; mine: boolean }>;
  /** YYYY-MM-DD, for an approved plan */
  nextReview: string | null;
};

export type PlanArea = {
  key: string;
  name: string;
  kind: FactorKind;
  dimension: FactorDimension;
  dimensionScore: number;
  consequence: number;
  likelihood: number;
  score: number;
  level: HmsRiskLevel;
  proposals: number;
  highlights: number;
  /** HR's deadline for a plan or a decision. Low risk has none. */
  deadline: string | null;
  decisions: AreaDecisionType[];
  decision: { decision: AreaDecisionType; reason: string | null; decidedBy: string | null; decidedAt: string } | null;
  plan: ActionPlan | null;
  weakestQuestion: RiskQuestion | null;
};

export type PlanOwner = { id: number; email: string; role: 'TEAM_LEADER' | 'SAFETY_REP' };

export type ActionPlanning = {
  /** The role the viewer acts in. The safety representative approves the plans. */
  role: 'TEAM_LEADER' | 'SAFETY_REP' | 'HR_ADMIN' | null;
  canChange: boolean;
  /** The team leader and the safety representative update the status while following up */
  canFollowUp: boolean;
  areas: PlanArea[];
  /** What the inspection report still waits for, as on the team leader's overview. Missing from older backends. */
  areasLeft?: AreaLeft[];
  proposals: Proposal[];
  owners: PlanOwner[];
  /** Why no one can approve the plans as safety representative. A team leader who also represents the team cannot. */
  /** notUsed where the organisation does not use safety representatives, and the team leader approves alone */
  reviewMissing: SafetyRepMissing | null;
  /** The main safety representatives who follow up because the team leader represents the team */
  takenOverBy?: string[] | null;
  /** The next surveys planned for the team, while following up */
  measurements: { safety: string | null; pulse: string | null } | null;
  /** Whether the evaluation can start, while following up */
  evaluation: EvaluationReadiness | null;
  /** Only the team leader starts the evaluation. Missing from older backends, which use canFollowUp. */
  canStartEvaluation?: boolean;
  canComplete: boolean;
};

export type ShareKind = 'PROPOSALS' | 'HIGHLIGHTS' | 'RESULT' | 'PLAN';

export type ChannelKind = 'teams' | 'slack' | 'email' | 'sms';

export type TeamShares = {
  canShare: boolean;
  channels: ChannelKind[];
  shareable: ShareKind[];
  shares: Array<{ kind: ShareKind; sharedAt: string; automatic: boolean }>;
};

export type TeamStep = 'survey' | 'assessed' | 'proposals' | 'plan' | 'followUp' | 'evaluation';

/** What the team does through its own link: propose or point out measures, or answer the evaluation */
export type TeamTask = 'propose' | 'highlight' | 'answer';

/** What the team sees through the shared link, without logging in */
export type TeamPage = {
  team: string;
  title: string;
  steps: Array<{ step: TeamStep; state: 'done' | 'current' | 'upcoming' }>;
  survey: { responses: number; expected: number; from: string; to: string };
  assessment: { at: string; withSafetyRep: boolean } | null;
  proposals: { count: number; voters: number; expected: number };
  plans: Array<{
    key: string;
    name: string;
    level: HmsRiskLevel | null;
    goal: string | null;
    measures: Array<{
      title: string;
      owner: 'TEAM_LEADER' | 'SAFETY_REP' | null;
      dueDate: string;
      status: MeasureStatus;
    }>;
    nextReview: string | null;
    evaluationDate: string | null;
    approvedAt: string;
    withSafetyRep: boolean;
  }>;
  otherAreas: Array<{
    key: string;
    name: string;
    level: HmsRiskLevel;
    decision: AreaDecisionType;
    deadline: string | null;
  }>;
  /** Highest risk first. Sensitive areas are shielded and have no level. */
  areas: Array<{ key: string; name: string; level: HmsRiskLevel | null }>;
  evaluationDate: string | null;
  whistleblowingUrl: string | null;
  safetyRep: string | null;
  /** Where the team is with the measures, or null outside those steps. In review the team waits for the leader. */
  measureStep: MeasureStep | null;
  /** What the team does now, with the survey whose link it uses. Null while others act. */
  task: { task: TeamTask; uuid: string; deadline: string | null } | null;
};

export type EvaluationReadiness = {
  canStart: boolean;
  reason: 'allDone' | 'dateReached' | null;
  /** YYYY-MM-DD, the first evaluation date of the plans */
  from: string | null;
};

export type AreaChange = 'better' | 'worse' | 'same';

export type AreaMeasurement = {
  level: HmsRiskLevel;
  /** The mean, 1–5, oriented to the dimension */
  score: number;
  /** 1–5, from the share who report a problem */
  likelihood: number;
  /** The team set the level in the risk assessment */
  adjusted: boolean;
};

/** Why the change and the risk level tell different stories, decided in the API */
export type LevelReading = 'meanOnly' | 'levelOnly' | 'opposite' | 'adjusted';

export type WorkEnvironmentEvaluation = {
  /** The team leader and the safety representative act. HR follows along. */
  canFollowUp: boolean;
  /** Only the team leader extends the deadline. Missing from older backends, which use canFollowUp. */
  canExtend?: boolean;
  evaluation: { id: number; uuid: string; status: WorkEnvironmentSurveyStatus; createdAt: string };
  baseline: { createdAt: string; responses: number; highRisk: number; areas: number };
  focus: Array<{
    key: string;
    name: string;
    level: HmsRiskLevel | null;
    goal: string | null;
    measures: number;
    done: number;
  }>;
  result: {
    status: 'assessed' | 'insufficientResponses';
    responses: number;
    areas: Array<{
      key: string;
      name: string;
      dimension: FactorDimension;
      before: AreaMeasurement;
      after: AreaMeasurement | null;
      change: AreaChange | null;
      reading: LevelReading | null;
    }>;
  } | null;
};

export type ResidualDecisionType = 'PLAN' | 'NEXT_SURVEY' | 'ACCEPT';

export type AcceptedBy = 'MANAGEMENT' | 'WORK_ENVIRONMENT_COMMITTEE' | 'TEAM_LEADER_AND_SAFETY_REP';

export type ResidualReason = 'stillHigh' | 'nowHigh' | 'worse';

type ResidualDecisionRecord = {
  decision: ResidualDecisionType;
  nextSurveyBy: string | null;
  owner: { id: number; email: string } | null;
  acceptedBy: AcceptedBy | null;
  reason: string | null;
  decidedBy: string | null;
  decidedAt: string;
};

/** The decision that stands, with the ones it replaced, newest first */
export type ResidualDecision = ResidualDecisionRecord & {
  earlier: ResidualDecisionRecord[];
};

/** A decision as the documentation keeps it, with the owner by email */
export type DocumentedResidualDecision = Omit<ResidualDecisionRecord, 'owner'> & {
  owner: string | null;
};

export type ResidualRisk = {
  key: string;
  name: string;
  level: HmsRiskLevel;
  reason: ResidualReason;
  before: AreaMeasurement;
  after: AreaMeasurement;
  decision: ResidualDecision | null;
};

export type ResidualRiskRequest = {
  decision: ResidualDecisionType;
  nextSurveyBy?: string | null;
  ownerId?: number | null;
  acceptedBy?: AcceptedBy | null;
  reason?: string | null;
};

export type ArchiveCheck =
  | 'survey'
  | 'assessment'
  | 'participation'
  | 'safetyRep'
  | 'measuresPlanned'
  | 'measuresDone'
  | 'evaluated'
  | 'combined'
  | 'shared'
  | 'residual';

/** Why the measures are not planned: the approved plans have none, or some lack an owner or a deadline */
export type MeasuresGap = 'noMeasures' | 'incomplete';

export type ArchiveSummary = {
  responses: number;
  expected: number;
  surveyedAt: string | null;
  areas: number;
  assessment: {
    at: string;
    participation: ReviewParticipation;
    representativeRole: string | null;
    absenceReason: string | null;
  } | null;
  proposals: number;
  voters: number;
  /** Why plans were approved without the safety representative */
  deviations: string[];
  measures: number;
  measuresDone: number;
  evaluatedAt: string | null;
  sharedResultAt: string | null;
  sharedPlanAt: string | null;
  signature: DocumentationSignature | null;
};

/** Why no one acts as safety representative for the team. A team leader who also represents it does not. */
export type SafetyRepMissing = 'leaderIsSafetyRep' | 'noSafetyRep' | 'notUsed';

export type WorkEnvironmentArchive = {
  canArchive: boolean;
  /** The safety representative decides the residual risk too. Missing from older backends, which use canArchive. */
  canDecideResidual?: boolean;
  /** Only the team leader asks the safety representative to sign. Missing from older backends, which use canArchive. */
  canRequestSignature?: boolean;
  archived: { at: string; by: string | null } | null;
  /** The survey the next round continues with, once an area got a new plan */
  nextRound: { surveyId: number } | null;
  residual: ResidualRisk[];
  /** The day and owner every area left to the next survey shares. Missing from older backends. */
  nextSurvey?: {
    nextSurveyBy: string;
    owner: { id: number; email: string } | null;
    areas: number;
  } | null;
  /** HR's interval from the evaluation: the team is surveyed again by then at the latest */
  latestNextSurveyBy?: string | null;
  owners: PlanOwner[];
  /** Without one, the team leader cannot sign or accept a risk with the safety representative */
  hasSafetyRep: boolean;
  safetyRepMissing: SafetyRepMissing | null;
  /** The main safety representatives who follow up because the team leader represents the team */
  takenOverBy?: string[] | null;
  /** Who signs for the team: its own safety representatives, or the main ones. Missing from older backends. */
  actingSafetyReps?: { emails: string[]; actsAs: 'own' | 'main' } | null;
  /** With a safety representative, only after they returned the documentation */
  canRegisterDeviation: boolean;
  acceptedBy: AcceptedBy[];
  checklist: Array<{ key: ArchiveCheck; done: boolean; required: boolean; gap?: MeasuresGap }>;
  summary: ArchiveSummary;
};

export type RequirementStatus = 'ok' | 'inProgress' | 'missing';

export type TeamRequirementKey =
  | 'surveyed'
  | 'assessed'
  | 'safetyRep'
  | 'highRiskPlan'
  | 'moderateDecision'
  | 'employees'
  | 'routines'
  | 'followUp';

/** The inspection report for one team, from the action plan on */
export type DocumentationLockReason = 'SIGNED' | 'ARCHIVED';

export type WorkEnvironmentDocumentation = {
  /** Archived work shows the report as it was locked. Null while it shows the current data. */
  locked: { at: string; reason: DocumentationLockReason } | null;
  generated: { at: string; by: string | null; role: 'TEAM_LEADER' | 'SAFETY_REP' | 'HR_ADMIN' | null };
  business: { name: string; orgNumber: string | null };
  /** False where the organisation has no safety representatives, and the report leaves them out */
  usesSafetyReps: boolean;
  team: {
    name: string;
    size: number;
    leaders: string[];
    safetyReps: string[];
    /** Why no one acts as safety representative. Missing from older backends. */
    safetyRepMissing?: SafetyRepMissing | null;
  };
  survey: {
    id: number;
    status: WorkEnvironmentSurveyStatus;
    createdAt: string;
    responseDeadline: string | null;
    riskCalculatedAt: string | null;
    actionPlanOpenedAt: string;
    responses: number;
    expected: number;
    minResponses: number;
    previousAt: string | null;
  };
  versions: {
    template: { version: number; factors: number };
    riskMatrix: { version: number; approvedAt: string | null; bySafetyRep: boolean } | null;
  };
  requirements: Array<{
    key: TeamRequirementKey;
    status: RequirementStatus;
    due: string | null;
    /** For the risk requirements, the areas still left. Reports locked before it was added do not have it. */
    areas?: string[];
  }>;
  assessment: {
    participation: ReviewParticipation;
    representativeRole: string | null;
    absenceReason: string | null;
    shortLongTerm: string;
    combined: string | null;
    savedAt: string;
    adjustments: number;
  } | null;
  areas: Array<{
    key: string;
    name: string;
    sensitive: boolean;
    calculated: HmsRiskLevel;
    assessed: HmsRiskLevel;
    adjustment: string | null;
    deadline: string | null;
    planned: boolean;
    decision: AreaDecisionType | null;
    after: HmsRiskLevel | null;
  }>;
  plans: Array<{
    areas: string[];
    frequency: PlanFrequency | null;
    evaluationDate: string | null;
    approvedAt: string | null;
    /** Null when the plan was approved before the approver was kept */
    bySafetyRep: boolean | null;
    deviation: string | null;
    reviews: number;
    nextReview: string | null;
  }>;
  measures: Array<{
    title: string;
    areas: string[];
    owner: string | null;
    dueDate: string;
    status: MeasureStatus;
    fromProposals: boolean;
  }>;
  measureCounts: { total: number; done: number; onTrack: number; overdue: number };
  routines: { whistleblowing: boolean; followUp: boolean };
  participation: {
    proposals: number;
    voters: number;
    dataSources: number;
    /** Reports locked before discussions were kept do not have them */
    discussions?: Array<Omit<ProposalDiscussion, 'id'>>;
  };
  evaluation: {
    evaluatedAt: string;
    responses: number;
    residual: Array<{
      name: string;
      level: HmsRiskLevel;
      decision: (DocumentedResidualDecision & { earlier: DocumentedResidualDecision[] }) | null;
    }>;
    nextSurveyBy: string | null;
  } | null;
  signature: Pick<DocumentationSignature, 'status' | 'requestedAt' | 'respondedAt' | 'respondedBy' | 'comment'> | null;
  activity: Array<{ id: number; action: string; role: string; at: string }>;
};

export type PlanRequest = {
  areas: Array<{ factorKey: string; goal: string }>;
  frequency: PlanFrequency | null;
  measureSafety: boolean;
  measurePulse: boolean;
  evaluationDate: string | null;
  measures: Array<{
    /** Kept measures keep their status */
    id?: number;
    title: string;
    description: string;
    ownerId: number | null;
    dueDate: string;
    proposalIds: number[];
    factorKeys: string[];
  }>;
};

export type PlanError =
  | { type: 'areas' | 'frequency' | 'evaluationDate' | 'measures' | 'deviation' }
  | { type: 'goal' | 'areaMeasures'; factorKey: string }
  | {
      type: 'measureTitle' | 'measureOwner' | 'measureDue' | 'measureProposals' | 'measureAreas';
      index: number;
    };

export type RiskActivity = {
  action:
    | 'RISK_ASSESSMENT_SAVED'
    | 'SAFETY_REP_REMINDED'
    | 'DEADLINE_EXTENDED'
    | 'INSUFFICIENT_ASSESSMENT_SAVED'
    | 'SURVEY_ARCHIVED'
    | 'DATA_SOURCE_ADDED'
    | 'DATA_SOURCE_REMOVED'
    | 'PROPOSALS_OPENED'
    | 'PROPOSAL_ADDED'
    | 'PROPOSAL_HIDDEN'
    | 'PROPOSALS_CLOSED'
    | 'HIGHLIGHTS_CLOSED'
    | 'PLAN_REVIEW_WITHDRAWN'
    | 'PLAN_REVIEW_REMINDED';
  role: 'TEAM_LEADER' | 'SAFETY_REP' | 'HR_ADMIN';
  email: string | null;
  at: string;
};

/** The risk is there once the survey has closed and it has been calculated */
export type WorkEnvironmentRisk =
  | { status: WorkEnvironmentSurveyStatus; calculatedAt: null; risk: null }
  | {
      status: WorkEnvironmentSurveyStatus;
      calculatedAt: string;
      lastResponseAt: string | null;
      activity: RiskActivity[];
      risk: RiskResult;
      dimensions: Record<FactorDimension, DimensionSummary | null> | null;
      /** At the risk step, when there are numbers */
      assessment: {
        canChange: boolean;
        elevatedCount: number;
        combinedAssessmentFrom: number;
        combinedRequired: boolean;
        /** Without them, the team leader assesses alone and is not asked who took part */
        usesSafetyReps: boolean;
        /** Those who act as safety representative for the team. Without them, the team leader contacts HR. */
        safetyReps: string[];
        safetyRepMissing: SafetyRepMissing | null;
        /** The main safety representatives who follow up because the team leader represents the team */
        takenOverBy?: string[] | null;
        hrEmails: string[];
        remindedAt: string | null;
        canRemind: boolean;
        saved: (RiskAssessment & SavedBy & { log: AdjustmentLogEntry[] }) | null;
      } | null;
      /** When too few answered */
      insufficient: {
        canDecide: boolean;
        attempts: number;
        maxExtensionDays: number;
        /** Who can assess it. Without a safety representative, a representative of the employees. */
        assessors: InsufficientAssessor[];
        /** Why no safety representative assesses it. Missing from older backends. */
        safetyRepMissing?: SafetyRepMissing | null;
        takenOverBy?: string[] | null;
        saved: (InsufficientAssessment & SavedBy) | null;
      } | null;
      /** After the assessment is saved */
      dataSources?: { canChange: boolean; items: DataSource[] };
    };

export type Measurement = {
  surveyId: number;
  measuredAt: string;
  /** Null for an evaluation that did not lead to a new plan */
  round: number | null;
  /** The round an evaluation measures again */
  evaluatesRound: number | null;
};

/** How the team leader sees a survey: a draft, on its way, archived, or stopped for too few answers */
export type SurveyOutcome = 'draft' | 'ongoing' | 'archived' | 'stopped';

/** One survey in the team leader's overview, with counts for the whole team only */
export type TeamSurveySummary = {
  id: number;
  title: string;
  status: WorkEnvironmentSurveyStatus;
  outcome: SurveyOutcome;
  createdAt: string;
  /** The round the survey starts, numbered from the oldest */
  round: number | null;
  /** Set on an evaluation that started a new round, to the round it evaluated */
  evaluatesRound: number | null;
  measuredAt: string | null;
  responses: number;
  expected: number | null;
  /** The fewest answers that give results, once the risk is calculated */
  minResponses: number | null;
  /** Null until the risk is calculated, and when too few answered */
  risk: { high: number; total: number } | null;
  /** The measures of the approved plans */
  measures: { done: number; total: number } | null;
  evaluationDate: string | null;
  /** When the evaluation of this survey was measured */
  evaluatedAt: string | null;
  /** What the team leader has to do next, while the survey is ongoing */
  next: NextDeadline | null;
  reports: { report: boolean; inspection: boolean };
  canDelete: boolean;
  /** The areas the inspection report still waits for, while plans can be made. High risk first, then by deadline. */
  areasLeft: AreaLeft[];
  /** Whether the user makes plans and decisions in the survey, as the team leader and HR do */
  canPlan: boolean;
};

/** An area at risk without an approved plan, or for moderate risk without a decision, as in the inspection report */
export type AreaLeft = {
  key: string;
  name: string;
  level: 'high' | 'moderate';
  deadline: string | null;
  /** Past HR's deadline, so the requirement is not met */
  overdue: boolean;
};

/** When a reminder goes out: right away, or when the working hours of the team start */
export type ReminderDelivery = { sendsAt: string };

/**
 * When the others hear about what the user did. Without a time nobody heard about it,
 * and without the field nobody was meant to.
 */
export type Notified = { sendsAt?: string | null };

export type PublicWorkEnvironmentSurvey = {
  uuid: string;
  title: string;
  description: string | null;
  isActive: boolean;
  questionnaire: Questionnaire;
  whistleblowingUrl: string | null;
  followUpRoutineUrl: string | null;
  /** Without one, the employee is told to speak to HR */
  hasSafetyRep: boolean;
  /** Who acts as safety representative for the team, so the employee knows who to speak to. Missing from older APIs. */
  safetyReps?: SafetyRepContact[];
  /** The team leader is the team's safety representative, so the main safety representative takes over */
  safetyRepTakenOver?: boolean;
};

export type SafetyRepContact = { name: string | null; email: string };
